import React, { useState, useEffect, useCallback } from "react";
import "./Master.scss";
import Branch from "./Branch";
import Department from "./Department";
import OfficeTimings from "./Office Timings/OfficeTimings";
import Announcement from "./Announcement";
import TermsAndConditions from "./TermsAndConditions";
import UserRole from "./UserRole";
import Holidays from "./Holidays";
import CompanyDetail from "./CompanyDetail";
import EmailConfiguration from "./EmailConfiguration";
import axiosInstance from "../../../axiosInstance";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import { toast } from "react-toastify";

const Master = () => {
  const pages = [
    "Company Details",
    "Department",
    "Branch",
    "Office Timings",
    "User Role",
    "Holidays",
    "Announcements",
    "Terms & Conditions",
    "Email Configuration",
  ];

  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [completedPages, setCompletedPages] = useState(
    pages.reduce((acc, page) => {
      acc[page] = false;
      return acc;
    }, {}),
  );

  const [selectedPage, setSelectedPage] = useState(pages[0]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  const [apiDataFetched, setApiDataFetched] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);

  // Stabilize markPageAsCompleted since it's a dependency for other functions
  const markPageAsCompleted = useCallback((page) => {
    setCompletedPages((prevState) => {
      if (prevState[page] === true) {
        return prevState;
      }
      return { ...prevState, [page]: true };
    });
  }, []);

  const fetchCompanyDetails = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/company/${process.env.REACT_APP_COMPANY_ID}`,
      );
      if (response.data?.data) {
        markPageAsCompleted("Company Details");
      }
    } catch (error) {
      console.error("Error fetching company details:", error);
    }
  }, [markPageAsCompleted]);

  const fetchUserRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/roles/all`,
      );
      const roles =
        response.data?.data?.map((role) => ({
          roleId: role.roleId || "N/A",
          roleName: role.roleName || "Unknown",
          roleSequence: role.roleSequence || 0,
          roleStatus: role.roleStatus || "UNKNOWN",
          companyName: role.companyName || "N/A",
        })) || [];
      const sortedRoles = roles.sort((a, b) => a.roleSequence - b.roleSequence);
      setUserRoles(sortedRoles);
      if (roles.length > 0) {
        markPageAsCompleted("User Role");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  }, [markPageAsCompleted]);

  const fetchDepartments = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/departments/all`,
      );
      const departmentsData = response.data?.data || [];
      if (departmentsData.length > 0) {
        markPageAsCompleted("Department");
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, [markPageAsCompleted]);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/branches`,
      );
      if (response.status === 200) {
        const branchesData = response.data?.data || [];
        if (branchesData.length > 0) {
          const normalizedBranches = branchesData.map((branch) => ({
            ...branch,
            status:
              branch.status?.toLowerCase() === "enabled"
                ? "Enabled"
                : "Disabled",
          }));
          const enabledBranches = normalizedBranches.filter(
            (branch) => branch.status === "Enabled",
          );
          if (enabledBranches.length >= 1) {
            markPageAsCompleted("Branch");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [markPageAsCompleted]);

  const fetchOfficeTimings = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/office-timings`,
      );
      if (response.data?.data && Object.keys(response.data.data).length > 0) {
        markPageAsCompleted("Office Timings");
      }
    } catch (error) {
      console.error("Error fetching office timings:", error);
    }
  }, [markPageAsCompleted]);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/holidays/all`,
      );
      if (Array.isArray(response.data?.data) && response.data.data.length > 0) {
        markPageAsCompleted("Holidays");
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  }, [markPageAsCompleted]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/announcements/${process.env.REACT_APP_COMPANY_ID}`,
      );
      if (response.data?.data) {
        markPageAsCompleted("Announcements");
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  }, [markPageAsCompleted]);

  const fetchTermsAndConditions = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/terms-and-conditions/terms-and-conditions`,
      );
      const data = response.data?.data || [];
      if (data.length > 0) {
        markPageAsCompleted("Terms & Conditions");
      }
    } catch (error) {
      console.error("Error fetching terms and conditions:", error);
    }
  }, [markPageAsCompleted]);

  const fetchEmailConfiguration = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/company/email-config/All`,
      );
      if (response.data?.data && Object.keys(response.data.data).length > 0) {
        markPageAsCompleted("Email Configuration");
      }
    } catch (error) {
      console.error("Error fetching email configuration:", error);
    }
  }, [markPageAsCompleted]);

  // Stabilize the main orchestrator function
  const fetchAllApiData = useCallback(async () => {
    if (apiDataFetched) return;
    setLoading(true);
    try {
      await Promise.all([
        fetchCompanyDetails(),
        fetchUserRoles(),
        fetchDepartments(),
        fetchBranches(),
        fetchOfficeTimings(),
        fetchHolidays(),
        fetchAnnouncements(),
        fetchTermsAndConditions(),
        fetchEmailConfiguration(),
      ]);

      setApiDataFetched(true);
    } catch (error) {
      console.error("Error fetching API data:", error);
    } finally {
      setLoading(false);
    }
  }, [
    apiDataFetched,
    fetchUserRoles,
    fetchDepartments,
    fetchBranches,
    fetchCompanyDetails,
    fetchOfficeTimings,
    fetchHolidays,
    fetchAnnouncements,
    fetchTermsAndConditions,
    fetchEmailConfiguration,
  ]);

  const totalPages = pages.length;
  const completedCount = Math.min(
    Object.values(completedPages).filter(Boolean).length,
    totalPages,
  );
  const progress = (completedCount / totalPages) * 100;

  useEffect(() => {
    fetchAllApiData();
  }, [fetchAllApiData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedPage]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    // 1. Return early if no file is selected.
    if (!selectedFile) return;

    // 2. Set loading state to true to show a spinner or disable the button.
    setLoading(true);

    try {
      // 3. Create a FormData object to send the file.
      const formData = new FormData();
      formData.append("file", selectedFile);

      // 4. Make the POST request to your API endpoint.
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/upload/csv`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      console.log(response);

      // 5. Handle a successful upload.
      if (response.status === 200) {
        setUploadSuccess(true);
        // Use optional chaining in case the setTemplateFile prop isn't passed.
        setTemplateFile?.(selectedFile);
        toast.success("File uploaded successfully!");

        // 6. Reset the state after a delay.
        setTimeout(() => {
          setUploadSuccess(false);
          // Use optional chaining in case the setShowUploadModal prop isn't passed.
          setShowUploadModal?.(false);
          setSelectedFile(null);
          // Optionally reload the page if needed to reflect changes.
          // window.location.reload();
        }, 3000);
      } else {
        // Handle non-200 success responses if your API does that.
        toast.error(`Upload failed with status: ${response.status}`);
      }
    } catch (error) {
      // 7. Handle errors gracefully.
      console.error("Error uploading file:", error);
      // Extract a specific error message from the API response if it exists, otherwise show a generic one.
      const errorMessage =
        error.response?.data?.message ||
        "An unexpected error occurred during the upload.";
      toast.error(errorMessage);
    } finally {
      // 8. Always set loading back to false, whether the upload succeeded or failed.
      setLoading(false);
    }
  };
  const downloadTemplate = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/upload/template`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "bulk_upload_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Template downloaded successfully!");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template");
    }
  };

  const downloadExistingTemplate = () => {
    if (templateFile) {
      const url = URL.createObjectURL(templateFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = templateFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setUploadSuccess(false);
  };

  const renderContent = () => {
    // Render the actual component if user has access
    switch (selectedPage) {
      case "Company Details":
        return (
          <CompanyDetail
            isEditing={isEditingCompany}
            setIsEditing={setIsEditingCompany}
          />
        );

      case "User Role":
        return (
          <UserRole
            userRoles={userRoles}
            setUserRoles={setUserRoles}
            markPageAsCompleted={markPageAsCompleted}
          />
        );

      case "Branch":
        return <Branch markPageAsCompleted={markPageAsCompleted} />;

      case "Department":
        return <Department markPageAsCompleted={markPageAsCompleted} />;

      case "Office Timings":
        return <OfficeTimings markPageAsCompleted={markPageAsCompleted} />;

      case "Holidays":
        return <Holidays markPageAsCompleted={markPageAsCompleted} />;

      case "Announcements":
        return <Announcement markPageAsCompleted={markPageAsCompleted} />;

      case "Terms & Conditions":
        return <TermsAndConditions markPageAsCompleted={markPageAsCompleted} />;

      case "Email Configuration":
        return <EmailConfiguration markPageAsCompleted={markPageAsCompleted} />;

      default:
        return <div>Select an item from the sidebar</div>;
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="master-container">
      <ButtonWithIcon
        className="file-upload-button"
        onClick={() => setShowUploadModal(true)}
        text="Upload Company Details"
      />

      {showUploadModal && (
        <div className="upload-modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Bulk Upload Company Details</h3>
              <button
                className="close-button"
                onClick={closeModal}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            <div className="modal-subheader">
              <p>
                Download the template, fill in the details, and upload the file
                below.
              </p>
            </div>

            <div className="modal-body">
              {uploadSuccess ? (
                <div className="success-message">
                  <i className="fas fa-check-circle success-icon"></i>
                  <p>File uploaded successfully!</p>
                </div>
              ) : (
                <>
                  <button
                    className="download-template-button"
                    onClick={downloadTemplate}
                  >
                    <i className="fas fa-download"></i> Download Template
                  </button>

                  <div className="file-input-container">
                    <label
                      htmlFor="master-file-upload"
                      className="file-upload-label"
                    >
                      <i className="fas fa-cloud-upload-alt"></i>
                      {selectedFile
                        ? selectedFile.name
                        : "Click to choose CSV or Excel file"}
                    </label>
                    <input
                      id="master-file-upload"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                  </div>

                  <button
                    className="upload-button"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                  >
                    <i className="fas fa-upload"></i> Upload File
                  </button>

                  <button className="cancel-button" onClick={closeModal}>
                    <i className="fas fa-times-circle"></i> Cancel
                  </button>
                </>
              )}

              {templateFile && (
                <div className="template-section">
                  <h4>Available Template</h4>
                  <div className="template-file">
                    <span>{templateFile.name}</span>
                    <button
                      className="download-button"
                      onClick={downloadExistingTemplate}
                    >
                      <i className="fas fa-download"></i> Download
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={`sidebar-container ${sidebarVisible ? "visible" : "hidden"}`}
      >
        <p className="admin-heading">Admin modules</p>
        <div className="progress-bar-container">
          <div className="progress-header">
            <h3>Setup Progress</h3>
            <span className="progress-percentage">{Math.round(progress)}%</span>
          </div>
          <div className="progress-bar-track">
            <div
              className="progress-bar"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-stats">
            <span className="completed-modules">
              {completedCount} modules configured
            </span>
            <span className="remaining-modules">
              {totalPages - completedCount}{" "}
              {totalPages - completedCount === 1 ? "remaining" : "remaining"}
            </span>
          </div>
          {progress < 100 && (
            <p className="demo-note text-xs text-gray-500 mt-2 italic">
              Note: Sample data has been pre-filled to help you get started —
              setup progress reflects demo configuration.
            </p>
          )}

          {progress === 100 && (
            <div className="completion-success">
              <div className="success-icon">✅</div>
              <div className="success-text">
                <p>
                  <strong>Setup Complete!</strong>
                </p>
                <p>Your HRM system is fully configured and ready to use</p>
              </div>
            </div>
          )}
        </div>
        <p className="scroll-instruction"> {">>"}</p>
        <ul className="sidebar-menu">
          {pages.map((page) => (
            <li
              key={page}
              className={`sidebar-item ${
                selectedPage === page ? "active" : ""
              } ${completedPages[page] ? "completed" : ""}`}
              onClick={() => setSelectedPage(page)}
            >
              <div className="sidebar-item-content">
                <span
                  className={`status-dot ${
                    completedPages[page] ? "completed" : "incomplete"
                  }`}
                ></span>
                {page}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <button
        className="sidebar-toggle-btn"
        onClick={() => setSidebarVisible(!sidebarVisible)}
      >
        &#8230;
      </button>
      <div
        className={`content-area ${
          sidebarVisible ? "sidebar-visible" : "sidebar-hidden"
        }`}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default Master;
